import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type {
  SourceSchoolYearEntry,
  TargetSchoolYearEntry,
} from '../api/migration-v1-v2';
import type { MigrationProject } from '../types';

/**
 * Extrait le message d'erreur le plus utile possible : on privilégie le
 * payload serveur (`response.data.error`) sur le message générique d'Axios
 * ("Request failed with status code 502") qui ne dit rien à l'utilisateur.
 */
function extractErrorMessage(e: unknown): string {
  if (e && typeof e === 'object') {
    const ax = e as { response?: { data?: { error?: string } }; message?: string };
    if (ax.response?.data?.error) return ax.response.data.error;
    if (ax.message) return ax.message;
  }
  return String(e);
}

interface Props {
  open: boolean;
  project: MigrationProject;
  onClose: () => void;
  onSaved: (updated: MigrationProject) => void;
}

export function ProjectConfigDialog({ open, project, onClose, onSaved }: Props) {
  const [sourceYears, setSourceYears] = useState<SourceSchoolYearEntry[]>([]);
  const [targetYears, setTargetYears] = useState<TargetSchoolYearEntry[]>([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [targetLoading, setTargetLoading] = useState(false);
  const [sourceError, setSourceError] = useState('');
  const [targetError, setTargetError] = useState('');

  const [sourceId, setSourceId] = useState<string>(
    project.sourceSchoolYearV1Id != null ? String(project.sourceSchoolYearV1Id) : ''
  );
  const [targetId, setTargetId] = useState<string>(project.targetSchoolYearV2Id ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSourceId(project.sourceSchoolYearV1Id != null ? String(project.sourceSchoolYearV1Id) : '');
    setTargetId(project.targetSchoolYearV2Id ?? '');
    setSaveError('');

    let cancelled = false;
    const loadSource = async () => {
      setSourceLoading(true);
      setSourceError('');
      try {
        const data = await migrationV1V2Api.getSourceSchoolYears(project.id);
        if (!cancelled) setSourceYears(data);
      } catch (e) {
        if (!cancelled) {
          setSourceError(extractErrorMessage(e) || 'Impossible de lister les années V1');
        }
      } finally {
        if (!cancelled) setSourceLoading(false);
      }
    };
    const loadTarget = async () => {
      setTargetLoading(true);
      setTargetError('');
      try {
        const data = await migrationV1V2Api.getTargetSchoolYears(project.id);
        if (!cancelled) setTargetYears(data);
      } catch (e) {
        if (!cancelled) {
          setTargetError(extractErrorMessage(e) || 'Impossible de lister les années V2');
        }
      } finally {
        if (!cancelled) setTargetLoading(false);
      }
    };
    loadSource();
    loadTarget();
    return () => {
      cancelled = true;
    };
  }, [open, project.id, project.sourceSchoolYearV1Id, project.targetSchoolYearV2Id]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const source = sourceYears.find((y) => String(y.id) === sourceId) ?? null;
      const target = targetYears.find((y) => y.id === targetId) ?? null;
      const updated = await migrationV1V2Api.updateProjectConfig(project.id, {
        sourceSchoolYearV1Id: source ? source.id : null,
        sourceSchoolYearV1Label: source ? source.label : null,
        targetSchoolYearV2Id: target ? target.id : null,
        targetSchoolYearV2Label: target ? target.label : null,
      });
      onSaved(updated);
      onClose();
    } catch (e) {
      setSaveError(extractErrorMessage(e) || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const canSave = sourceId !== '' && targetId !== '' && !saving;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Configuration de la migration</DialogTitle>
          <DialogDescription>
            Choisissez l'année scolaire source (V1) et l'année cible (V2). La migration
            sera scopée sur ce couple d'années — la majorité des référentiels V2 dépendent
            de l'année scolaire.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Source */}
          <div className="space-y-2">
            <Label>Année source (V1)</Label>
            {sourceLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement des années V1…
              </div>
            ) : sourceError ? (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{sourceError}</span>
              </div>
            ) : (
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une année V1" />
                </SelectTrigger>
                <SelectContent>
                  {sourceYears.map((y) => {
                    const normalize = (s: string) => s.replace(/\s+/g, '').toLowerCase();
                    const showTitle = y.title && normalize(y.title) !== normalize(y.label);
                    return (
                      <SelectItem key={y.id} value={String(y.id)}>
                        {y.label}
                        {showTitle ? ` — ${y.title}` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Target */}
          <div className="space-y-2">
            <Label>Année cible (V2)</Label>
            {targetLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement des années V2…
              </div>
            ) : targetError ? (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{targetError}</span>
              </div>
            ) : targetYears.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                Aucune année scolaire disponible côté V2. Vérifiez que le tenant est bien
                provisionné.
              </p>
            ) : (
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une année V2" />
                </SelectTrigger>
                <SelectContent>
                  {targetYears.map((y) => (
                    <SelectItem key={y.id ?? ''} value={y.id ?? ''} disabled={!y.id}>
                      {y.label ?? '(sans label)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {saveError && (
            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{saveError}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
